/**
 * Intro della homepage (vedi snippets/pa-intro.liquid).
 * Fasi: accensione del logo (CSS) → volo verso la proiezione dell'hero → passaggio di consegne
 * al proiettore, che si accende quando riceve l'evento `pa:intro-landed`.
 */
(() => {
  const root = document.documentElement;
  const overlay = document.getElementById('PaIntro');
  if (!overlay || !root.classList.contains('pa-intro')) {
    root.classList.remove('pa-intro');
    overlay?.remove();
    return;
  }

  // Lo script è arrivato: prende il posto della sicura del <head> con un limite proprio, più ampio,
  // perché ogni attesa qui sotto è già limitata (lampo, scena 3D ≤ 1,5 s, volo, dissolvenza).
  clearTimeout(window.__paIntroSafety);
  const safety = setTimeout(() => finish(), 10000);

  const logo = overlay.querySelector('.pa-intro__logo');
  const HOLD_MS = 280; // pausa dopo il lampo, prima del volo
  const WAIT_3D_MS = 1500; // attesa massima della scena 3D
  const FLY_MS = 950;
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  let landed = false;
  let done = false;

  const land = () => {
    if (landed) return;
    landed = true;
    root.classList.remove('pa-intro');
    document.dispatchEvent(new CustomEvent('pa:intro-landed'));
  };

  const finish = () => {
    if (done) return;
    done = true;
    try {
      sessionStorage.setItem('pa-intro-seen', '1');
    } catch (e) {
      /* navigazione privata: pazienza, l'intro potrà ripetersi */
    }
    clearTimeout(safety);
    land();
    overlay.remove();
  };

  /**
   * Angoli a schermo della proiezione (TL, TR, BR, BL): esatti dalla scena 3D,
   * altrimenti il riquadro del logo nella versione CSS.
   */
  function targetQuad() {
    const projector = document.querySelector('pa-projector');
    const quad = projector?.scene3d?.logoQuad?.();
    if (quad) return quad;
    const r = projector?.querySelector('.pa-stage__slide.is-active :is(svg, img)')?.getBoundingClientRect();
    if (!r || !r.width) return null;
    return [
      [r.left, r.top],
      [r.right, r.top],
      [r.right, r.bottom],
      [r.left, r.bottom],
    ];
  }

  const onScreen = (quad) => quad.some(([x, y]) => x >= 0 && y >= 0 && x <= window.innerWidth && y <= window.innerHeight);

  /**
   * Omografia che porta il rettangolo (0,0)-(w,h) sui 4 punti dati, come matrix3d CSS
   * (transform-origin 0 0). Metodo di Heckbert, quadrato unitario → quadrilatero.
   */
  function rectToQuad(w, h, [[x0, y0], [x1, y1], [x2, y2], [x3, y3]]) {
    const sx = x0 - x1 + x2 - x3;
    const sy = y0 - y1 + y2 - y3;
    let g = 0;
    let k = 0;
    if (Math.abs(sx) > 1e-9 || Math.abs(sy) > 1e-9) {
      const dx1 = x1 - x2;
      const dy1 = y1 - y2;
      const dx2 = x3 - x2;
      const dy2 = y3 - y2;
      const den = dx1 * dy2 - dx2 * dy1;
      g = (sx * dy2 - dx2 * sy) / den;
      k = (dx1 * sy - sx * dy1) / den;
    }
    const a = x1 - x0 + g * x1;
    const b = x3 - x0 + k * x3;
    const d = y1 - y0 + g * y1;
    const e = y3 - y0 + k * y3;
    return `matrix3d(${a / w}, ${d / w}, 0, ${g / w}, ${b / h}, ${e / h}, 0, ${k / h}, 0, 0, 1, 0, ${x0}, ${y0}, 0, 1)`;
  }

  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  async function fly() {
    if (done) return;
    overlay.classList.add('is-leaving');
    const first = targetQuad();
    if (!first || !onScreen(first)) {
      await overlay.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 500, fill: 'forwards' }).finished;
      return finish();
    }

    // Il logo vola "per angoli": ogni frame interpola i 4 vertici verso la proiezione
    // (ricalcolata in tempo reale) e applica l'omografia corrispondente.
    const from = logo.getBoundingClientRect();
    const { width: w, height: h } = from;
    const start = [
      [0, 0],
      [w, 0],
      [w, h],
      [0, h],
    ];
    logo.style.transformOrigin = '0 0';
    logo.style.willChange = 'transform';

    const t0 = performance.now();
    let fadeStarted = false;
    await new Promise((resolve) => {
      const step = (now) => {
        if (done) return resolve();
        const p = Math.min(1, (now - t0) / FLY_MS);
        const e = easeInOut(p);
        const quad = targetQuad() || first;
        const corners = quad.map(([x, y], i) => [
          start[i][0] + (x - from.left - start[i][0]) * e,
          start[i][1] + (y - from.top - start[i][1]) * e,
        ]);
        logo.style.transform = rectToQuad(w, h, corners);
        if (p === 1 && !fadeStarted) {
          fadeStarted = true;
          // Il proiettore si accende; il logo in volo sfuma mentre compare la proiezione vera.
          land();
          logo.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 700, delay: 450, fill: 'forwards' }).finished.then(resolve);
        }
        requestAnimationFrame(step); // continua a seguire il bersaglio anche durante la dissolvenza
      };
      requestAnimationFrame(step);
    });
    finish();
  }

  /** Aspetta la fine reale del lampo (le animazioni CSS partono al primo rendering dell'overlay). */
  async function flashDone() {
    const sweep = overlay
      .getAnimations({ subtree: true })
      .filter((a) => a.animationName === 'pa-intro-sweep' || a.animationName === 'pa-intro-word');
    await Promise.all(sweep.map((a) => a.finished.catch(() => {})));
    await sleep(HOLD_MS);
  }

  /** Se la scena 3D si sta caricando, le lascia un attimo: il logo deve volare verso di lei. */
  async function sceneReady() {
    const projector = document.querySelector('pa-projector');
    const start = performance.now();
    while (projector?.classList.contains('is-3d-pending') && performance.now() - start < WAIT_3D_MS) {
      await sleep(60);
    }
  }

  overlay.querySelector('.pa-intro__skip')?.addEventListener('click', finish);
  document.addEventListener('keydown', (e) => e.key === 'Escape' && finish());
  flashDone()
    .then(sceneReady)
    .then(fly)
    .catch(finish);
})();
